/**
 * Driver Cabinet API client
 */
import axios, { AxiosInstance } from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_URL !== undefined && import.meta.env.VITE_API_URL !== null
    ? String(import.meta.env.VITE_API_URL)
    : '';

const SESSION_USER_KEY = 'driverUser';
const SESSION_TOKEN_KEY = 'driverToken';

/** API root — always from site origin, not /driver/ path. */
function driverAppApiBase(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (fromEnv !== undefined && fromEnv !== null && String(fromEnv).trim() !== '') {
    const base = String(fromEnv).replace(/\/$/, '');
    // VITE_API_URL=/api/v1 (same-origin admin) → /api/v1/app, not /api/v1/api/v1/app
    if (base.endsWith('/api/v1')) {
      return `${base}/app`;
    }
    return `${base}/api/v1/app`;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/v1/app`;
  }
  return '/api/v1/app';
}

export interface DriverUser {
  iin: string;
  fio?: string | null;
}

export interface DriverTopup {
  id: string;
  amount: number;
  createdAt: string;
  status: string;
}

export interface DriverMe extends DriverUser {
  workStatus?: string | null;
  isSelfemployed?: boolean | null;
  employmentType?: string | null;
  balance?: number;
  topups?: DriverTopup[];
  demo?: boolean;
  billing?: {
    period: string;
    payer: 'park' | 'driver';
    parkName?: string | null;
    amount?: number;
    status?: 'none' | 'invoiced' | 'paid' | 'overdue' | 'voided';
    dueAt?: string | null;
    blocked?: boolean;
    message?: string;
  };
}

export interface DriverDocument {
  id: string;
  companyId?: string;
  yandexId?: string;
  status: string;
  documentType?: string;
  source?: 'yandex' | 'park';
  priceTotal?: number;
  sellerName?: string;
  sellerIin?: string;
  buyerName?: string;
  buyerIin?: string;
  fio?: string;
  iin?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
  hasPdf?: boolean;
  waitingKey?: string;
  statusLabel?: string;
  billing?: DriverMe['billing'];
  signatureMarks?: {
    DRIVER?: boolean;
    ACCOUNTANT?: boolean;
    YANDEX?: boolean;
  };
}

export interface EgovAuthStartResponse {
  sessionId: string;
  eGovMobileLaunchLink: string | null;
  qrCode: string | null;
  expiresAt: string;
}

export interface EgovAuthSessionResponse {
  id: string;
  status: 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'CANCELED';
  iin?: string | null;
  fio?: string | null;
  eGovMobileLaunchLink?: string | null;
  qrCode?: string | null;
  token?: string;
  expiresAt: string;
}

export interface SignSessionResponse {
  sessionId: string;
  expiresAt: string;
  eGovMobileLaunchLink: string | null;
  qrCode: string | null;
  count: number;
}

export interface SignSessionStatusResponse {
  sessionId: string;
  status: 'PENDING' | 'COMPLETED' | 'PARTIAL' | 'EXPIRED';
  documentIds: string[];
  expiresAt: string;
  completedAt?: string | null;
}

class DriverApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: driverAppApiBase(),
      timeout: 120000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(SESSION_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const message = error?.response?.data?.error || error?.message || 'Ошибка запроса';
        return Promise.reject(new Error(message));
      }
    );
  }

  getStoredUser(): DriverUser | null {
    try {
      const raw = localStorage.getItem(SESSION_USER_KEY);
      return raw ? (JSON.parse(raw) as DriverUser) : null;
    } catch {
      return null;
    }
  }

  isLoggedIn(): boolean {
    return Boolean(this.getStoredUser() && localStorage.getItem(SESSION_TOKEN_KEY));
  }

  setSession(user: DriverUser, token: string) {
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }

  clearSession() {
    localStorage.removeItem(SESSION_USER_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
  }

  async getDemoLogin(): Promise<{ enabled: boolean }> {
    const response = await this.client.get<{ enabled: boolean }>('auth/demo');
    return response.data;
  }

  async loginDemo(code: string): Promise<{ token: string; iin: string; fio: string }> {
    const response = await this.client.post<{ token: string; iin: string; fio: string }>(
      'auth/demo',
      { code }
    );
    return response.data;
  }

  async startEgovAuth(): Promise<EgovAuthStartResponse> {
    const response = await this.client.post<EgovAuthStartResponse>('auth/egov/start', {});
    return response.data;
  }

  async getEgovAuthSession(sessionId: string): Promise<EgovAuthSessionResponse> {
    const response = await this.client.get<EgovAuthSessionResponse>(`auth/egov/${sessionId}`);
    return response.data;
  }

  async getMe(): Promise<DriverMe> {
    const response = await this.client.get<DriverMe>('me');
    return response.data;
  }

  async startKaspiPayment(opts?: { qr?: boolean }): Promise<{
    action: string;
    form: {
      TranId: string;
      OrderId: string;
      Amount: string;
      Service: string;
      returnUrl: string;
    };
    url: string | null;
    qrCodeImage: string | null;
    tranId: string;
    orderId: string;
    amount: number;
    amountTiyn: number;
    mocked?: boolean;
  }> {
    const response = await this.client.post('billing/kaspi', { qr: Boolean(opts?.qr) });
    return response.data;
  }

  async startProcardPayment(): Promise<{
    url: string | null;
    orderId: string;
    amount: number;
    currency: string;
    mocked?: boolean;
  }> {
    const response = await this.client.post<{
      url: string | null;
      orderId: string;
      amount: number;
      currency: string;
      mocked?: boolean;
    }>('billing/procard');
    return response.data;
  }

  async getDocuments(status: 'to_sign' | 'ALL' = 'to_sign') {
    const response = await this.client.get<{
      count: number;
      iin: string;
      billing?: DriverMe['billing'];
      documents: DriverDocument[];
    }>(`documents?status=${status}`);
    return response.data;
  }

  async getDocument(id: string) {
    const response = await this.client.get<DriverDocument & { pdfBase64?: string }>(
      `documents/${id}`
    );
    return response.data;
  }

  async createSignSession(documentIds: string[]): Promise<SignSessionResponse> {
    const response = await this.client.post<SignSessionResponse>('documents/sign-session', {
      documentIds,
    });
    return response.data;
  }

  async getSignSession(sessionId: string): Promise<SignSessionStatusResponse> {
    const response = await this.client.get<SignSessionStatusResponse>(
      `documents/sign-session/${sessionId}`
    );
    return response.data;
  }

  async fetchPdfBlob(id: string): Promise<Blob> {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    const base = API_BASE_URL.trim() || window.location.origin;
    const url = `${base.replace(/\/$/, '')}/api/v1/app/documents/${id}/pdf`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('PDF недоступен');
    return response.blob();
  }

  async openPdf(id: string) {
    const blob = await this.fetchPdfBlob(id);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  }
}

export const driverApi = new DriverApiService();
